/**
 * Closed value sets for first-party funnel properties.
 *
 * The ingest route (app/api/funnel/event/route.ts) checks incoming values
 * against these, because it cannot trust what a browser sent: a property that
 * is meant to be a bucket must never carry free text into funnel_events. The
 * client trackers in lib/ga4.ts take the same types, so a new value is added
 * here once and both sides agree.
 */
import { TESLA_FACTS } from './tesla-facts'

export const DOWNLOAD_SOURCES = [
  'sound_player',
  'saved_page',
  'mobile_player',
  'create_page',
  'voice_generator',
  'sound_card',
  'preview_completion_cta',
  'command_palette',
  'blog_embed',
] as const
export type DownloadSource = (typeof DOWNLOAD_SOURCES)[number]

export const DOWNLOAD_FAILURE_REASONS = ['network', 'http_4xx', 'http_5xx', 'blocked', 'timeout'] as const
export type DownloadFailureReason = (typeof DOWNLOAD_FAILURE_REASONS)[number]

export const CREATE_FAILURE_REASONS = ['torn_header', 'decode', 'export', 'timeout', 'error'] as const

/** `create_step` reports a voice API refusal as `http_<status>`. */
const HTTP_STATUS_REASON_RE = /^http_[1-5]\d{2}$/

const REASONS: ReadonlySet<string> = new Set([...DOWNLOAD_FAILURE_REASONS, ...CREATE_FAILURE_REASONS])

export const CREATE_TOOLS = ['convert', 'voice'] as const
export const CREATE_STEPS = ['file_selected', 'converted', 'failed', 'voice_generated'] as const
export const ZERO_RESULTS_ACTIONS = ['request_sound', 'vote_request', 'browse_all', 'clear_filters'] as const

export const GUIDE_SECTIONS = ['install', 'troubleshooting', 'best_sounds', 'compatibility', 'faq', 'related', 'cta'] as const
export type GuideSection = (typeof GUIDE_SECTIONS)[number]

export const GUIDE_PROBLEMS = ['default_chime', 'usb_missing', 'quiet_or_distorted', 'menu_missing', 'restore_stock'] as const
export type GuideProblem = (typeof GUIDE_PROBLEMS)[number]

/** Where the in-flow newsletter offer was shown (audit X9). */
export const OFFER_SURFACES = ['receipt', 'return_visit'] as const
export type OfferSurface = (typeof OFFER_SURFACES)[number]

export const CAR_CHECK_VERDICTS = ['yes', 'check', 'unlikely'] as const

export const AD_SLOT_STATES = ['filled', 'unfilled', 'unresolved'] as const
export type AdSlotState = (typeof AD_SLOT_STATES)[number]

/** Model years the "Check my car" picker can offer. */
const MODEL_YEAR_MIN = 2012
const MODEL_YEAR_MAX = 2100

const AD_SLOT_ID_RE = /^\d{6,20}$/
const MAX_RESULT_COUNT = 1_000_000

function inSet(values: readonly string[]) {
  const set: ReadonlySet<string> = new Set(values)
  return (value: unknown): boolean => typeof value === 'string' && set.has(value)
}

// The library filter reports the canonical label ("Model Y"); the guide picker
// reports the facts key ("model-y"). Both name the same five vehicles.
const isModel = inSet(
  Object.entries(TESLA_FACTS.support.models).flatMap(([key, model]) => [key, model.label]),
)
export const isDownloadSource = inSet(DOWNLOAD_SOURCES)

/**
 * Property key -> predicate. A key listed here is dropped from the stored row
 * when its value fails the check; the event itself is kept so counts hold.
 * Keys not listed are passed through as before.
 *
 * `source` is not here: it also carries playback, search and guide sources that
 * have never been a closed set. The four download events are, and the route
 * checks theirs with `isDownloadSource`.
 */
export const FUNNEL_PROPERTY_CHECKS: Record<string, (value: unknown) => boolean> = {
  model: (v) => v === '' || isModel(v),
  reason: (v) => typeof v === 'string' && (REASONS.has(v) || HTTP_STATUS_REASON_RE.test(v)),
  tool: inSet(CREATE_TOOLS),
  step: inSet(CREATE_STEPS),
  action: inSet(ZERO_RESULTS_ACTIONS),
  section: inSet(GUIDE_SECTIONS),
  problem: inSet(GUIDE_PROBLEMS),
  verdict: inSet(CAR_CHECK_VERDICTS),
  state: inSet(AD_SLOT_STATES),
  offer_surface: inSet(OFFER_SURFACES),
  ad_slot: (v) => typeof v === 'string' && AD_SLOT_ID_RE.test(v),
  rescued: (v) => v === true,
  result_count: (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_RESULT_COUNT,
  model_year: (v) => typeof v === 'number' && Number.isInteger(v) && v >= MODEL_YEAR_MIN && v <= MODEL_YEAR_MAX,
}
